import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  TextField, 
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Chip,
  Stack,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { ReportApi } from '../../api/ReportApi';
import { useEffect } from 'react';
import { patientApi } from '../../api/patientApi';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hospitalApi } from '../../api/hospitalApi';
import { Patient } from '../../features/patients/types/patient.types';
import PaymentModeBreakdown from './components/PaymentModeBreakdown';

type TransactionType = 'appointment' | 'service' | 'payment' | 'refund';
type TransactionStatus = 'pending' | 'completed' | 'cancelled' | 'refunded';

interface AppointmentOrder {
  id: string;
  itemName: string;
  quantity: number;
  price: number;
  grandTotal: number;
  paidAmount: number;
  financialStatus: string;
}

interface AppointmentDetail {
  id: string;
  status: string;
  doctorName: string;
  doctorId: string;
  appointmentDate: string;
  orders: AppointmentOrder[];
}

interface PatientStatementSummary {
  patientId: string;
  patientName: string;
  totalAppointments: number;
  totalOrders: number;
  totalOrderValue: number;
  totalPaid: number;
  totalPending: number;
  appointments?: AppointmentDetail[];
  paymentModes?: Record<string, number>;
}

interface PatientStatement {
  id: string;
  patientId: string;
  patientName: string;
  date: string;
  description: string;
  charges: number;
  payments: number;
  balance: number;
  status: 'paid' | 'unpaid' | 'partial';
  doctorId?: string;
  doctorName?: string;
  details: {
    date: string;
    description: string;
    charges: number;
    payments: number;
    doctorId?: string;
    doctorName?: string;
  }[];
}

interface Transaction {
  id: string;
  date: Date;
  type: TransactionType;
  description: string;
  amount: number;
  status: TransactionStatus;
  referenceId: string;
  doctorId?: string;
  doctorName?: string;
}

interface PatientStatementProps {
  patientId?: string;
}

const PatientStatementReport: React.FC<PatientStatementProps> = ({ patientId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Initialize dates in local timezone to avoid timezone conversion issues
  const initializeDefaultDates = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { startOfDay, endOfDay };
  };
  
  const { startOfDay: defaultStartDate, endOfDay: defaultEndDate } = initializeDefaultDates();
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate);
  const [selectedPatient, setSelectedPatient] = useState<string>(patientId || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PatientStatementSummary[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  
  // PDF and hospital info
  const reportRef = React.useRef<HTMLDivElement>(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  
  // Payment summary state
  const [paymentSummary, setPaymentSummary] = useState<any>(null);
  const [appointmentStatusSummary, setAppointmentStatusSummary] = useState<any>(null);

  useEffect(() => {
    async function fetchPatients() {
      try {
        const res = await patientApi.getPatients({ page: 1, limit: 100 });
        setPatients(res.data);
      } catch {
        setPatients([]);
      }
    }
    fetchPatients();
  }, []);

  // Fetch hospital info on component mount
  useEffect(() => {
    const fetchHospitalInfo = async () => {
      try {
        const hospital = await hospitalApi.getCompanyProfile();
        setHospitalInfo(hospital);
      } catch (error) {
        console.error('Error fetching hospital info:', error);
      }
    };
    fetchHospitalInfo();
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (!startDate || !endDate) return;
      setLoading(true);
      try {
        // Format dates in local timezone to avoid timezone conversion issues
        const formatDateForAPI = (date: Date) => {
          // Use local timezone methods to avoid UTC conversion
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          
          // Return ISO-like format but in local timezone
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        };

        const result = await ReportApi.getPatientStatementReport({
          fromDate: formatDateForAPI(startDate),
          toDate: formatDateForAPI(endDate),
          patientId: selectedPatient || undefined,
        });
        setData(result);
        
        // Fetch payment and appointment status summaries
        await fetchPaymentSummary(result);
        await fetchAppointmentStatusSummary(result);
      } catch (e) {
        setData([]);
        setPaymentSummary(null);
        setAppointmentStatusSummary(null);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [startDate, endDate, selectedPatient]);

  // Fetch payment summary for all appointments in the data
  const fetchPaymentSummary = async (reportData: PatientStatementSummary[]) => {
    try {
      const { appointmentOrdersApi } = await import('../../features/appointments/api/appointmentOrdersApi');
      
      // Extract appointment IDs from the report data
      const appointmentIds: string[] = [];
      console.log('Report Data for Payment Summary:', reportData);
      reportData.forEach(patient => {
        console.log('Patient data:', patient);
        // Use any type for appointments since the structure may vary
        (patient as any).appointments?.forEach((appointment: any) => {
          console.log('Appointment data:', appointment);
          if (appointment.id) {
            appointmentIds.push(appointment.id);
          }
        });
      });
      console.log('Extracted appointment IDs:', appointmentIds);

      if (appointmentIds.length === 0) {
        setPaymentSummary({ cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0 });
        return;
      }

      const summaries = await Promise.all(
        appointmentIds.map(async (id) => {
          try {
            return await appointmentOrdersApi.getPaymentCollectionsSummary(id);
          } catch (error) {
            return { cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0 };
          }
        })
      );

      const totalSummary = summaries.reduce((acc, summary) => {
        const data = summary?.data || summary;
        acc.cash += data.cash || 0;
        acc.card += data.card || 0;
        acc.upi += data.upi || 0;
        acc.netbanking += data.netbanking || 0;
        acc.insurance += data.insurance || 0;
        acc.waveOff += data.waveOff || 0;
        acc.other += data.other || 0;
        acc.total += data.total || 0;
        return acc;
      }, { cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0 });

      console.log('Payment Summary Data:', totalSummary);
      setPaymentSummary(totalSummary);
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      setPaymentSummary({ cash: 0, card: 0, upi: 0, netbanking: 0, insurance: 0, waveOff: 0, other: 0, total: 0 });
    }
  };

  // Fetch appointment status summary
  const fetchAppointmentStatusSummary = async (reportData: PatientStatementSummary[]) => {
    try {
      const statusCounts = {
        SCHEDULED: 0,
        ARRIVED: 0,
        IN_PROGRESS: 0,
        COMPLETED: 0,
        CANCELLED: 0,
        NO_SHOW: 0
      };

      reportData.forEach(patient => {
        (patient as any).appointments?.forEach((appointment: any) => {
          if (appointment.status && statusCounts.hasOwnProperty(appointment.status)) {
            statusCounts[appointment.status as keyof typeof statusCounts]++;
          }
        });
      });

      console.log('Appointment Status Summary Data:', statusCounts);
      setAppointmentStatusSummary(statusCounts);
    } catch (error) {
      console.error('Error calculating appointment status summary:', error);
      setAppointmentStatusSummary(null);
    }
  };

  // Aggregate for summary cards - exclude cancelled and refunded orders
  const filteredData = data.map(row => {
    // Filter appointments and their orders to exclude cancelled/refunded
    const filteredAppointments = row.appointments?.map(appointment => ({
      ...appointment,
      orders: appointment.orders.filter(order => 
        order.financialStatus !== 'cancelled' && 
        order.financialStatus !== 'refunded'
      )
    })).filter(appointment => appointment.orders.length > 0) || [];

    // Recalculate totals based on filtered orders
    const filteredOrderValue = filteredAppointments.reduce((sum, appointment) => 
      sum + appointment.orders.reduce((orderSum, order) => orderSum + order.grandTotal, 0), 0
    );
    const filteredPaidAmount = filteredAppointments.reduce((sum, appointment) => 
      sum + appointment.orders.reduce((orderSum, order) => orderSum + order.paidAmount, 0), 0
    );
    const filteredPendingAmount = filteredOrderValue - filteredPaidAmount;
    const filteredOrderCount = filteredAppointments.reduce((sum, appointment) => sum + appointment.orders.length, 0);

    return {
      ...row,
      appointments: filteredAppointments,
      totalOrderValue: filteredOrderValue,
      totalPaid: filteredPaidAmount,
      totalPending: filteredPendingAmount,
      totalOrders: filteredOrderCount
    };
  }).filter(row => row.totalOrders > 0); // Only show patients with active orders
  
  const totalAppointments = filteredData.reduce((sum, row) => sum + row.totalAppointments, 0);
  const totalOrderValue = filteredData.reduce((sum, row) => sum + row.totalOrderValue, 0);
  const totalPaid = filteredData.reduce((sum, row) => sum + row.totalPaid, 0);
  const totalPending = filteredData.reduce((sum, row) => sum + row.totalPending, 0);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (patientId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(patientId)) {
      newExpandedRows.delete(patientId);
    } else {
      newExpandedRows.add(patientId);
    }
    setExpandedRows(newExpandedRows);
  };

  const getStatusColor = (status: TransactionStatus) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  const generateExcelReport = () => {
    // Create Excel data structure
    const reportData = [];
    
    // Header information
    reportData.push(['Patient Statement Report']);
    reportData.push(['Generated on:', new Date().toLocaleDateString('en-IN')]);
    reportData.push(['Date Range:', `${startDate?.toLocaleDateString('en-IN')} to ${endDate?.toLocaleDateString('en-IN')}`]);
    reportData.push(['Selected Patient:', selectedPatient ? patients.find(p => p.id === selectedPatient)?.fullName || 'All Patients' : 'All Patients']);
    reportData.push([]);
    
    // Summary cards data
    reportData.push(['SUMMARY']);
    reportData.push(['Total Appointments', totalAppointments]);
    reportData.push(['Total Order Value', `${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`]);
    reportData.push(['Total Paid', `${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`]);
    reportData.push(['Total Pending', `${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`]);
    reportData.push([]);
    
    // Table headers
    reportData.push(['PATIENT DETAILS']);
    reportData.push(['Patient Name', 'Appointment Orders(appointments)', 'Total Order Value', 'Total Paid', 'Total Pending', 'Line Total']);
    
    // Table data
    filteredData.forEach(row => {
      reportData.push([
        row.patientName,
        `${row.totalOrders} (${row.totalAppointments})`,
        `${row.totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        `${row.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        `${row.totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        `${row.totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
      ]);
    });
    
    // Total row
    reportData.push([
      'TOTAL',
      `${filteredData.reduce((sum, row) => sum + row.totalOrders, 0)} (${totalAppointments})`,
      `${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      `${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      `${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      `${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    ]);
    
    // Convert to CSV format
    const csvContent = reportData.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\r\n');
    
    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `PatientStatement_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('patient-statement-report');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Patient Statement Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .summary-cards { display: flex; justify-content: space-around; margin: 20px 0; }
                .summary-card { text-align: center; padding: 15px; border: 1px solid #ddd; border-radius: 5px; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f5f5f5; font-weight: bold; }
                .total-row { background-color: #f5f5f5; font-weight: bold; }
                .summary-section { margin-top: 30px; }
                .summary-box { display: inline-block; margin: 10px; padding: 15px; border: 1px solid #ddd; border-radius: 5px; text-align: center; }
                @media print { .no-print { display: none; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Patient Statement Report</h1>
                <p>Generated on: ${new Date().toLocaleDateString('en-IN')}</p>
                <p>Date Range: ${startDate?.toLocaleDateString('en-IN')} to ${endDate?.toLocaleDateString('en-IN')}</p>
                <p>Patient: ${selectedPatient ? patients.find(p => p.id === selectedPatient)?.fullName || 'All Patients' : 'All Patients'}</p>
              </div>
              
              <div class="summary-cards">
                <div class="summary-card">
                  <h3>Total Appointments</h3>
                  <h2>${totalAppointments}</h2>
                </div>
                <div class="summary-card">
                  <h3>Total Order Value</h3>
                  <h2>₹${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                </div>
                <div class="summary-card">
                  <h3>Total Paid</h3>
                  <h2>₹${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                </div>
                <div class="summary-card">
                  <h3>Total Pending</h3>
                  <h2>₹${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                </div>
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Appointment Orders(appointments)</th>
                    <th>Total Order Value</th>
                    <th>Total Paid</th>
                    <th>Total Pending</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${filteredData.map(row => `
                    <tr>
                      <td>${row.patientName}</td>
                      <td>${row.totalOrders} (${row.totalAppointments})</td>
                      <td>₹${row.totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>₹${row.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>₹${row.totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>₹${row.totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  `).join('')}
                  <tr class="total-row">
                    <td><strong>TOTAL</strong></td>
                    <td><strong>${filteredData.reduce((sum, row) => sum + row.totalOrders, 0)} (${totalAppointments})</strong></td>
                    <td><strong>₹${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></td>
                    <td><strong>₹${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></td>
                    <td><strong>₹${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></td>
                    <td><strong>₹${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></td>
                  </tr>
                </tbody>
              </table>
              
              <div class="summary-section">
                <h3>Financial Summary</h3>
                <div class="summary-box">
                  <h4>Total Order Value</h4>
                  <h3>₹${totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                </div>
                <div class="summary-box">
                  <h4>Total Paid</h4>
                  <h3>₹${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                </div>
                <div class="summary-box">
                  <h4>Total Pending</h4>
                  <h3>₹${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  // PDF download functionality
  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current) return;
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Use hospital info from API or fallback to default
      const pdfHospitalInfo = hospitalInfo || {
        name: "Airyaman Healthcare",
        address: "123 Healthcare Street, Medical District, City - 123456",
        phoneNumber: "+91 12345 67890",
        email: "info@airyaman.com"
      };
      
      // Header section with logo
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            ctx?.drawImage(logoImg, 0, 0);
            const logoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            pdf.addImage(logoDataUrl, 'JPEG', 20, 10, 16, 16);
            resolve(true);
          };
          logoImg.onerror = () => {
            console.warn('Logo not found, continuing without logo');
            resolve(false);
          };
          logoImg.src = '/logo.jpeg';
        });
      } catch (error) {
        console.warn('Error loading logo:', error);
      }
      
      // Hospital name and info
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(pdfHospitalInfo.name, 40, 20);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const address = pdfHospitalInfo.address || 'Address not available';
      const phone = pdfHospitalInfo.phoneNumber || 'Phone not available';
      const email = pdfHospitalInfo.email || 'Email not available';
      
      pdf.text(address, 40, 28);
      pdf.text(`Phone: ${phone}`, 40, 35);
      pdf.text(`Email: ${email}`, 40, 42);
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Patient Statement Report', 20, 65);
      
      // Date range
      const fromDateStr = startDate ? startDate.toLocaleDateString() : 'N/A';
      const toDateStr = endDate ? endDate.toLocaleDateString() : 'N/A';
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${fromDateStr} to ${toDateStr}`, 20, 75);
      
      // Add line separator
      pdf.line(20, 80, 190, 80);
      
      // Create canvas from the report content
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          const allElements = clonedDoc.querySelectorAll('*');
          allElements.forEach((element: any) => {
            if (element.style) {
              element.style.color = 'black';
              element.style.setProperty('color', 'black', 'important');
            }
          });
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 170;
      const pageHeight = 200;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 85;
      
      // Add first page content
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(imgHeight, pageHeight));
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Patient Statement Report (Continued)', 20, 20);
        pdf.line(20, 25, 190, 25);
        
        position = heightLeft - imgHeight + 30;
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(heightLeft, 240));
        heightLeft -= 240;
      }
      
      const filename = `PatientStatement_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }} ref={reportRef} id="patient-statement-report">
        <Typography variant="h4" gutterBottom>
          Patient Statement
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View detailed patient statement including appointments and services
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={patients}
                  getOptionLabel={(option) => option.fullName || ''}
                  value={patients.find(p => p.id === selectedPatient) || null}
                  onChange={(_, newValue) => setSelectedPatient(newValue ? newValue.id : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Patient" fullWidth size="small" />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      onKeyDown: (e) => e.preventDefault(), // Prevent typing
                      onPaste: (e) => e.preventDefault(), // Prevent pasting
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      onKeyDown: (e) => e.preventDefault(), // Prevent typing
                      onPaste: (e) => e.preventDefault(), // Prevent pasting
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={generateExcelReport}
                    sx={{ height: '40px', flex: 1 }}
                  >
                    Export Excel
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                    sx={{ height: '40px', flex: 1 }}
                  >
                    PDF
                  </Button>
                  <Menu
                    anchorEl={pdfMenuAnchor}
                    open={Boolean(pdfMenuAnchor)}
                    onClose={() => setPdfMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Current Page Only" />
                    </MenuItem>
                    <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Complete Report" />
                    </MenuItem>
                  </Menu>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    startIcon={<RefreshIcon />}
                    onClick={() => window.location.reload()}
                    sx={{ height: '40px', flex: 1 }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards - Moved to top */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Patients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {totalAppointments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Appointments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="secondary.main">
                  {filteredData.reduce((sum, row) => sum + row.totalOrders, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  ₹{totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Order Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  ₹{totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Paid
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  ₹{totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Doctor Wise Summary */}
        {filteredData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Doctor Wise Summary
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  const doctorSummary: Record<string, { appointments: number; orders: number; orderValue: number; paid: number; pending: number }> = {};
                  
                  filteredData.forEach(patient => {
                    if (patient.appointments) {
                      patient.appointments.forEach(appointment => {
                        const doctorName = appointment.doctorName || 'Unknown Doctor';
                        if (!doctorSummary[doctorName]) {
                          doctorSummary[doctorName] = { appointments: 0, orders: 0, orderValue: 0, paid: 0, pending: 0 };
                        }
                        
                        doctorSummary[doctorName].appointments += 1;
                        doctorSummary[doctorName].orders += appointment.orders.length;
                        
                        appointment.orders
                          .filter(order => order.financialStatus !== 'cancelled' && order.financialStatus !== 'refunded')
                          .forEach(order => {
                            doctorSummary[doctorName].orderValue += order.grandTotal;
                            doctorSummary[doctorName].paid += order.paidAmount;
                            doctorSummary[doctorName].pending += (order.grandTotal - order.paidAmount);
                          });
                      });
                    }
                  });
                  
                  return Object.entries(doctorSummary).map(([doctorName, summary]) => (
                    <Grid item xs={12} md={6} lg={4} key={doctorName}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" color="primary" gutterBottom>
                            {doctorName}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Appointments: {summary.appointments}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Orders: {summary.orders}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Order Value: ₹{summary.orderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="success.main">
                                Paid: ₹{summary.paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="warning.main">
                                Pending: ₹{summary.pending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ));
                })()}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Appointment Status Breakdown */}
        {filteredData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Appointment Status Breakdown
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  const statusCounts: Record<string, number> = {};
                  filteredData.forEach(patient => {
                    if (patient.appointments && patient.appointments.length > 0) {
                      patient.appointments.forEach(appointment => {
                        statusCounts[appointment.status] = (statusCounts[appointment.status] || 0) + 1;
                      });
                    }
                  });
                  
                  console.log('Status counts:', statusCounts);
                  console.log('Filtered data:', filteredData);
                  
                  // If no data from backend, show sample data for testing
                  if (Object.keys(statusCounts).length === 0) {
                    return (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No appointment status data available. This will show once the backend provides detailed appointment information.
                        </Typography>
                      </Grid>
                    );
                  }
                  
                  return Object.entries(statusCounts).map(([status, count]) => (
                    <Grid item xs={12} sm={6} md={3} key={status}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {count}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {status.replace('_', ' ')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ));
                })()}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Payment Mode Breakdown */}
        {filteredData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Mode Breakdown
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  const paymentModes: Record<string, number> = {};
                  filteredData.forEach(patient => {
                    if (patient.paymentModes && Object.keys(patient.paymentModes).length > 0) {
                      Object.entries(patient.paymentModes).forEach(([mode, amount]) => {
                        paymentModes[mode] = (paymentModes[mode] || 0) + amount;
                      });
                    }
                  });
                  
                  console.log('Payment modes:', paymentModes);
                  
                  return (
                    <PaymentModeBreakdown 
                      filteredData={filteredData} 
                      backendPaymentModes={paymentModes} 
                    />
                  );
                })()}
              </Grid>
            </CardContent>
          </Card>
        )}

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Patient Name</TableCell>
                  <TableCell align="right">Appointment Orders(appointments)</TableCell>
                  <TableCell align="right">Total Order Value</TableCell>
                  <TableCell align="right">Total Paid</TableCell>
                  <TableCell align="right">Total Pending</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                    const lineTotal = row.totalOrderValue;
                    const isExpanded = expandedRows.has(row.patientId);
                    return (
                      <React.Fragment key={`${row.patientId}-${index}`}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => toggleRowExpansion(row.patientId)}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.patientName}</TableCell>
                          <TableCell align="right">{row.totalOrders} ({row.totalAppointments})</TableCell>
                          <TableCell align="right">₹{row.totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">₹{row.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">₹{row.totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                  Appointment Details
                                </Typography>
                                {row.appointments && row.appointments.length > 0 ? (
                                  <Table size="small" aria-label="appointments">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Doctor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Orders</TableCell>
                                        <TableCell align="right">Value</TableCell>
                                        <TableCell align="right">Paid</TableCell>
                                        <TableCell align="right">Pending</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.appointments.map((appointment) => (
                                        <TableRow key={appointment.id}>
                                          <TableCell>
                                            {new Date(appointment.appointmentDate).toLocaleDateString('en-IN')}
                                          </TableCell>
                                          <TableCell>{appointment.doctorName}</TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={appointment.status.replace('_', ' ')} 
                                              size="small"
                                              color={appointment.status === 'COMPLETED' ? 'success' : 
                                                     appointment.status === 'CANCELLED' ? 'error' : 'default'}
                                            />
                                          </TableCell>
                                          <TableCell align="right">{appointment.orders.length}</TableCell>
                                          <TableCell align="right">
                                            ₹{appointment.orders
                                              .filter(order => order.financialStatus !== 'cancelled' && order.financialStatus !== 'refunded')
                                              .reduce((sum, order) => sum + order.grandTotal, 0)
                                              .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                          </TableCell>
                                          <TableCell align="right">
                                            ₹{appointment.orders
                                              .filter(order => order.financialStatus !== 'cancelled' && order.financialStatus !== 'refunded')
                                              .reduce((sum, order) => sum + order.paidAmount, 0)
                                              .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                          </TableCell>
                                          <TableCell align="right">
                                            ₹{appointment.orders
                                              .filter(order => order.financialStatus !== 'cancelled' && order.financialStatus !== 'refunded')
                                              .reduce((sum, order) => sum + (order.grandTotal - order.paidAmount), 0)
                                              .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No appointment details available
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data found for the selected criteria
                    </TableCell>
                  </TableRow>
                )}
                {/* Summary Row */}
                {filteredData.length > 0 && (
                  <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{filteredData.reduce((sum, row) => sum + row.totalOrders, 0)} ({totalAppointments})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{totalOrderValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination Controls - Moved below data grid for better UX */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            slotProps={{
              actions: {
                previousButton: { style: { textDecoration: 'none' } },
                nextButton: { style: { textDecoration: 'none' } }
              }
            }}
            sx={{
              '& .MuiTablePagination-actions button': {
                textDecoration: 'none'
              },
              '& .MuiTablePagination-input': {
                display: 'none' // Hide page input to prevent manual typing
              }
            }}
          />
        </Paper>

      </Box>
    </LocalizationProvider>
  );
};

export default PatientStatementReport;
